import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGraduationCap,
  faCertificate,
  faTools,
  faWrench,
  faCogs,
  faBuildingShield
} from '@fortawesome/free-solid-svg-icons';
import { parse, differenceInMonths } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';  // Import locales
import { useTranslation } from '../hooks/useTranslation';
import '../styles/Experience.css';
import vinciLogo from '../assets/images/logos/vinci.webp'
import atoleLogo from '../assets/images/logos/atole.webp'
import grtLogo from '../assets/images/logos/grtgaz.png'

const Experience = () => {
  const { t, currentLanguage } = useTranslation();

  // Get the correct date-fns locale based on currentLanguage
  const getLocale = () => {
    switch (currentLanguage) {
      case 'en': return enUS;
      case 'es': return es;
      case 'fr':
      default: return fr;
    }
  };

  const locale = getLocale();

  const getPeriodDuration = (startDate, endDate) => {
    try {
      const start = parse(startDate, 'MMMM yyyy', new Date(), { locale });
      const end = endDate === t('experience.present')
        ? new Date()
        : parse(endDate, 'MMMM yyyy', new Date(), { locale });

      if (isNaN(start) || isNaN(end)) {
        // Fallback for "YYYY - YYYY" format for education entries
        const startYear = parseInt(startDate.split(' ').pop(), 10);
        const endYear = parseInt(endDate.split(' ').pop(), 10);
        if (!isNaN(startYear) && !isNaN(endYear)) {
          const years = endYear - startYear;
          if (years > 0) {
            return `${years} ${years > 1 ? t('experience.yearsPlural') : t('experience.years')}`;
          }
        }
        return ""; // Return empty if parsing fails
      }

      const totalMonths = differenceInMonths(end, start);
      const years = Math.floor(totalMonths / 12);
      const months = totalMonths % 12;

      const yearsText = years > 0 ? `${years} ${years > 1 ? t('experience.yearsPlural') : t('experience.years')}` : '';
      const monthsText = months > 0 ? `${months} ${t('experience.months')}` : '';

      return `${yearsText} ${monthsText}`.trim() || `0 ${t('experience.months')}`;
    } catch (error) {
      console.error("Error parsing dates:", error);
      return "";
    }
  };

  const experiences = [
    {
      id: 0,
      title: t('experience.experiences.vinciErp.title'),
      company: t('experience.experiences.vinciErp.company'),
      period: t('experience.experiences.vinciErp.period'),
      globalPeriod: t('experience.vinciGlobalPeriod'),
      description: t('experience.experiences.vinciErp.description'),
      skills: t('experience.experiences.vinciErp.skills'),
      icon: faBuildingShield,
      type: 'work',
      logo: vinciLogo
    },
    {
      id: 1,
      title: t('experience.experiences.vinci.title'),
      company: t('experience.experiences.vinci.company'),
      period: t('experience.experiences.vinci.period'),
      globalPeriod: t('experience.vinciGlobalPeriod'),
      description: t('experience.experiences.vinci.description'),
      skills: t('experience.experiences.vinci.skills'),
      icon: faCogs,
      type: 'work',
      logo: vinciLogo
    },
    {
      id: 2,
      title: t('experience.experiences.atole.title'),
      company: t('experience.experiences.atole.company'),
      period: t('experience.experiences.atole.period'),
      description: t('experience.experiences.atole.description'),
      skills: t('experience.experiences.atole.skills'),
      icon: faWrench,
      type: 'work',
      logo: atoleLogo
    },
    {
      id: 3,
      title: t('experience.experiences.grtgaz.title'),
      company: t('experience.experiences.grtgaz.company'),
      period: t('experience.experiences.grtgaz.period'),
      description: t('experience.experiences.grtgaz.description'),
      skills: t('experience.experiences.grtgaz.skills'),
      icon: faTools,
      type: 'work',
      logo: grtLogo
    },
    {
      id: 4,
      title: t('experience.experiences.bts.title'),
      company: t('experience.experiences.bts.company'),
      period: t('experience.experiences.bts.period'),
      description: t('experience.experiences.bts.description'),
      skills: t('experience.experiences.bts.skills'),
      icon: faGraduationCap,
      type: 'education'
    },
    {
      id: 5,
      title: t('experience.experiences.bac.title'),
      company: t('experience.experiences.bac.company'),
      period: t('experience.experiences.bac.period'),
      description: t('experience.experiences.bac.description'),
      skills: t('experience.experiences.bac.skills'),
      icon: faGraduationCap,
      type: 'education'
    },
    {
      id: 6,
      title: t('experience.experiences.certifications.title'),
      company: t('experience.experiences.certifications.company'),
      period: t('experience.experiences.certifications.period'),
      description: t('experience.experiences.certifications.description'),
      skills: t('experience.experiences.certifications.skills'),
      icon: faCertificate,
      type: 'certification'
    },
    {
      id: 7,
      title: t('experience.experiences.technical.title'),
      company: t('experience.experiences.technical.company'),
      period: t('experience.experiences.technical.period'),
      description: t('experience.experiences.technical.description'),
      skills: t('experience.experiences.technical.skills'),
      icon: faTools,
      type: 'skills'
    }
  ];

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <section id="experience" className="experience-section">
      <motion.h2
        className="section-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {t('experience.title')}
      </motion.h2>

      <div className="timeline-container">
        <div className="timeline-track"></div>

        {experiences.map((exp, index) => {
          // For VINCI entries, use the global period for duration calculation
          const periodForDuration = exp.globalPeriod || exp.period;
          const startPeriod = periodForDuration.split(" - ")[0];
          const endPeriod = periodForDuration.split(" - ")[1];
          const periodDuration = getPeriodDuration(startPeriod, endPeriod);
          const isWork = exp.type === 'work';
          return (
            <motion.div
              key={exp.id}
              className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'} ${exp.type}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <motion.div 
                className="timeline-icon"
                whileHover={!isMobile ? { scale: 1.2, rotate: [0, -10, 10, -10, 0] } : {}}
                whileTap={{ scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                <FontAwesomeIcon icon={exp.icon} />
              </motion.div>

              <div className="timeline-content">
                <div className="timeline-header">
                  <h3>{exp.title}</h3>
                  {exp.globalPeriod ? (
                    <span className="timeline-period">
                      {exp.period}<br />
                      <small style={{ opacity: 0.8 }}>{t('experience.vinciGlobalLabel')}: {exp.globalPeriod} : {periodDuration}</small>
                    </span>
                  ) : (
                    <span className="timeline-period">
                      {isWork ? `${exp.period} : ${periodDuration}` : exp.period}
                    </span>
                  )}
                </div>

                <div className="timeline-company">
                  {exp.logo && (
                    <img
                      src={exp.logo}
                      alt={`${exp.company} logo`}
                      className="company-logo"
                      loading="lazy"
                    />
                  )}
                  <span>{exp.company}</span>
                </div>

                <p className="timeline-description" style={{ whiteSpace: 'pre-line' }}>
                  {exp.description}
                </p>

                <div className="timeline-skills">
                  {exp.skills.map(skill => (
                    <span key={skill} className="skill-tag">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default Experience;